// src/components/CreditBalance.tsx
import React, { useState, useEffect } from 'react';
import { creditsApi } from '../services/api';
import { useNavigate } from 'react-router-dom';

const CreditBalance: React.FC = () => {
  const [balance, setBalance] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState<string | null>(null);
  const navigate = useNavigate();

  // Monitor token availability
  useEffect(() => {
    const checkToken = () => {
      const accessToken = localStorage.getItem('access_token');
      const authToken = sessionStorage.getItem('auth_token');
      const currentToken = accessToken || authToken;
      setToken(currentToken);
    };

    checkToken();

    // Check token periodically (every 500ms) for the first few seconds after login
    const tokenCheckInterval = setInterval(checkToken, 500);

    // Stop checking after 5 seconds
    const stopCheckTimeout = setTimeout(() => {
      clearInterval(tokenCheckInterval);
    }, 5000);

    return () => {
      clearInterval(tokenCheckInterval);
      clearTimeout(stopCheckTimeout);
    };
  }, []);

  useEffect(() => {
    // Only fetch if we have a token
    if (!token) {
      console.log('[CreditBalance] No token available yet, skipping fetch');
      return;
    }

    fetchBalance();

    // Refresh balance every 30 seconds
    const interval = setInterval(fetchBalance, 30000);

    return () => clearInterval(interval);
  }, [token]);

  const fetchBalance = async () => {
    try {
      const data = await creditsApi.getBalance();
      setBalance(data.credit_balance);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch credit balance:', error);
      setLoading(false);
    }
  };

  const getBalanceColor = () => {
    if (balance === null) return 'text-gray-500';
    if (balance < 20) return 'text-red-500';
    if (balance < 50) return 'text-yellow-500';
    return 'text-green-500';
  };

  const getBalanceIcon = () => {
    if (balance === null) return '💳';
    if (balance < 20) return '⚠️';
    if (balance < 50) return '⚡';
    return '✨';
  };

  const handleClick = () => {
    navigate('/settings?tab=credits');
  };

  if (loading) {
    return (
      <div className="flex items-center space-x-2 px-3 py-1 rounded-lg bg-gray-100 dark:bg-gray-800">
        <span className="text-sm text-gray-500">Loading...</span>
      </div>
    );
  }

  return (
    <button
      onClick={handleClick}
      className="flex items-center space-x-2 px-3 py-1 rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors cursor-pointer"
      title="View credit details"
    >
      <span className="text-lg">{getBalanceIcon()}</span>
      <span className={`text-sm font-semibold ${getBalanceColor()}`}>
        {balance !== null ? balance.toLocaleString() : '—'}
      </span>
      <span className="text-xs text-gray-500">credits</span>
    </button>
  );
};

export default CreditBalance;
